import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  Globe,
  DollarSign,
  Calendar,
  AlertTriangle,
  Info,
  Download,
  CheckCircle,
  Clock,
  Leaf,
  Loader2,
  Settings,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Shuffle,
  Target,
  Truck,
  Building2,
  Home,
  Users,
  HelpCircle,
  MessageSquare,
  ArrowRight,
  MapPin,
  Shield,
  Award,
} from "lucide-react";
import { Card, Button, Badge, Input, PriceComparison } from "@bcc/shared-ui";
import { formatCurrency } from "@bcc/shared-ui";
import { SEO } from "../components/SEO";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8002";

// ============================================================================
// TARIFF RATES (January 2026)
// Sources: Canada.ca, Blake, Cassels & Graydon LLP, JD Supra
// ============================================================================

interface TariffRates {
  usa: {
    steel: number;
    aluminum: number;
    lumber: number;
    cabinets: number;
    general: number;
    shipping: number;
  };
  china: {
    general: number;
    steel: number;
    shipping: number;
    customsBrokerage: number;
  };
}

// Material to tariff category mapping
const MATERIAL_TARIFF_MAP: Record<
  string,
  {
    usa: "steel" | "aluminum" | "lumber" | "cabinets" | "general";
    china: "general" | "steel";
  }
> = {
  flooring: { usa: "lumber", china: "general" },
  doors: { usa: "lumber", china: "general" },
  cabinets: { usa: "cabinets", china: "general" },
  tubs_showers: { usa: "general", china: "general" },
  fixtures: { usa: "steel", china: "steel" },
  countertops: { usa: "general", china: "general" },
  appliances: { usa: "steel", china: "general" },
  windows: { usa: "aluminum", china: "general" },
  trim: { usa: "lumber", china: "general" },
};

interface MaterialCategory {
  id: string;
  name: string;
  icon: string;
  selected: boolean;
}

const materialCategories: MaterialCategory[] = [
  { id: "flooring", name: "Flooring", icon: "🪵", selected: false },
  { id: "doors", name: "Doors", icon: "🚪", selected: false },
  { id: "cabinets", name: "Cabinets", icon: "🗄️", selected: false },
  { id: "tubs_showers", name: "Tubs & Showers", icon: "🛁", selected: false },
  { id: "fixtures", name: "Fixtures", icon: "💡", selected: false },
  { id: "countertops", name: "Countertops", icon: "🪨", selected: false },
  { id: "appliances", name: "Appliances", icon: "🏠", selected: false },
  { id: "windows", name: "Windows", icon: "🪟", selected: false },
  { id: "trim", name: "Trim & Millwork", icon: "📐", selected: false },
];

interface Incentive {
  name: string;
  type: "federal" | "provincial" | "municipal";
  benefit: string;
  description: string;
}

// Fallback incentives if API fails
const fallbackIncentives: Incentive[] = [
  {
    name: "Build in Canada Innovation Program",
    type: "federal",
    benefit: "Up to 15% procurement preference",
    description:
      "Federal program encouraging Canadian content in government contracts",
  },
  {
    name: "Alberta Manufacturing Investment Tax Credit",
    type: "provincial",
    benefit: "10% non-refundable tax credit",
    description: "For capital investments in Alberta manufacturing",
  },
  {
    name: "SR&ED Tax Credit",
    type: "federal",
    benefit: "15-35% refundable credit",
    description: "For innovative materials or construction methods",
  },
  {
    name: "Green Building Incentive",
    type: "municipal",
    benefit: "Permit fee reduction up to 50%",
    description: "For projects using sustainable, locally-sourced materials",
  },
];

// OR Technique definitions with icons - Simplified to 4 most useful techniques
interface ORTechnique {
  id: string;
  name: string;
  shortName: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  userQuestion: string; // The question this technique answers for users
}

const orTechniques: ORTechnique[] = [
  {
    id: "monte-carlo",
    name: "Cost Risk Analysis",
    shortName: "Risk Analysis",
    icon: Shuffle,
    description: "Understand cost uncertainty across sourcing options",
    userQuestion: "What's my budget risk?",
  },
  {
    id: "decision-tree",
    name: "Sourcing Decision",
    shortName: "Best Source",
    icon: Target,
    description: "Compare countries with tariff & supply risk scenarios",
    userQuestion: "Where should I source from?",
  },
  {
    id: "jit",
    name: "Order Scheduling",
    shortName: "When to Order",
    icon: Truck,
    description: "Optimal delivery dates to minimize storage costs",
    userQuestion: "When should I order materials?",
  },
  {
    id: "timeline",
    name: "Project Timeline",
    shortName: "Timeline",
    icon: BarChart3,
    description: "Estimate project duration with uncertainty",
    userQuestion: "How long will my project take?",
  },
];

// API call functions for feasibility endpoints
async function fetchFeasibilityDefaults() {
  const response = await fetch(`${API_BASE}/api/v1/feasibility/defaults`);
  if (!response.ok) throw new Error("Failed to fetch defaults");
  return response.json();
}

async function runFullAnalysis(budget: number, timelineDays: number) {
  const response = await fetch(`${API_BASE}/api/v1/feasibility/analyze`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ budget, timeline_days: timelineDays }),
  });
  if (!response.ok) throw new Error("Failed to run analysis");
  return response.json();
}

async function runPERTAnalysis(params: {
  optimistic: number;
  most_likely: number;
  pessimistic: number;
}) {
  const response = await fetch(`${API_BASE}/api/v1/feasibility/pert`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!response.ok) throw new Error("Failed to run PERT analysis");
  return response.json();
}

async function runCPMAnalysis(
  tasks?: Array<{
    id: string;
    name: string;
    duration: number;
    dependencies: string[];
  }>,
) {
  const response = await fetch(`${API_BASE}/api/v1/feasibility/cpm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tasks: tasks || [] }),
  });
  if (!response.ok) throw new Error("Failed to run CPM analysis");
  return response.json();
}

async function runMonteCarloAnalysis(params: {
  budget: number;
  num_simulations: number;
}) {
  const response = await fetch(`${API_BASE}/api/v1/feasibility/monte-carlo`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!response.ok) throw new Error("Failed to run Monte Carlo analysis");
  return response.json();
}

async function runDecisionTreeAnalysis() {
  const response = await fetch(`${API_BASE}/api/v1/feasibility/decision-tree`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!response.ok) throw new Error("Failed to run decision tree analysis");
  return response.json();
}

async function runJITAnalysis(params: {
  project_start?: string;
  storage_cost_per_day: number;
}) {
  const response = await fetch(`${API_BASE}/api/v1/feasibility/jit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!response.ok) throw new Error("Failed to run JIT analysis");
  return response.json();
}

// Mock data generators for when API is not available
function generateMockComparison(
  category: MaterialCategory,
  budget: number,
  tariffs: TariffRates,
) {
  // Realistic base prices by category (CAD per unit)
  const basePrices: Record<string, number> = {
    flooring: 45, // per SF
    doors: 350, // per door
    cabinets: 280, // per LF
    tubs_showers: 800, // each
    fixtures: 150, // each
    countertops: 120, // per SF
    appliances: 1200, // each
    windows: 450, // each
    trim: 25, // per LF
  };

  const basePrice = basePrices[category.id] || 200;
  const quantity = Math.round(budget / 10 / basePrice);

  // Get applicable tariff rates for this material
  const tariffMapping = MATERIAL_TARIFF_MAP[category.id] || {
    usa: "general" as const,
    china: "general" as const,
  };
  const usaTariffRate = tariffs.usa[tariffMapping.usa];
  const chinaTariffRate = tariffs.china[tariffMapping.china];

  // Calculate landed costs with tariffs
  // Canada: No tariff, local sourcing
  const canadaBaseCost = basePrice * quantity;
  const canadaCost = canadaBaseCost;

  // USA: Base price 15% lower, but add tariff + shipping
  const usaBaseUnitCost = basePrice * 0.85;
  const usaBaseCost = usaBaseUnitCost * quantity;
  const usaTariffCost = usaBaseCost * (usaTariffRate / 100);
  const usaShippingCost = usaBaseCost * (tariffs.usa.shipping / 100);
  const usaCost = usaBaseCost + usaTariffCost + usaShippingCost;

  // China: Base price 45% lower, but add tariff + shipping + customs
  const chinaBaseUnitCost = basePrice * 0.55;
  const chinaBaseCost = chinaBaseUnitCost * quantity;
  const chinaTariffCost = chinaBaseCost * (chinaTariffRate / 100);
  const chinaShippingCost = chinaBaseCost * (tariffs.china.shipping / 100);
  const chinaCustomsCost =
    chinaBaseCost * (tariffs.china.customsBrokerage / 100);
  const chinaCost =
    chinaBaseCost + chinaTariffCost + chinaShippingCost + chinaCustomsCost;

  const comparisons = [
    {
      country: "Canada",
      unit_cost: basePrice,
      base_cost: Math.round(canadaBaseCost),
      tariff_cost: 0,
      tariff_rate: 0,
      shipping_cost: 0,
      total_landed_cost: Math.round(canadaCost),
      lead_time_days: 14,
    },
    {
      country: "USA",
      unit_cost: Math.round(usaBaseUnitCost),
      base_cost: Math.round(usaBaseCost),
      tariff_cost: Math.round(usaTariffCost),
      tariff_rate: usaTariffRate,
      shipping_cost: Math.round(usaShippingCost),
      total_landed_cost: Math.round(usaCost),
      lead_time_days: 21,
    },
    {
      country: "China",
      unit_cost: Math.round(chinaBaseUnitCost),
      base_cost: Math.round(chinaBaseCost),
      tariff_cost: Math.round(chinaTariffCost),
      tariff_rate: chinaTariffRate,
      shipping_cost: Math.round(chinaShippingCost + chinaCustomsCost),
      total_landed_cost: Math.round(chinaCost),
      lead_time_days: 60,
    },
  ];

  // Determine recommendation based on total cost and incentives
  const canadaWithIncentives = canadaCost * 0.85; // 15% effective savings with incentives
  const bestOption =
    canadaWithIncentives < usaCost && canadaWithIncentives < chinaCost
      ? "Canada (with incentives)"
      : usaCost < chinaCost
        ? "USA"
        : "China";

  return {
    comparisons,
    tariffNote:
      usaTariffRate > 0
        ? `USA ${tariffMapping.usa} tariff: ${usaTariffRate}% • China tariff: ${chinaTariffRate}%`
        : `China tariff: ${chinaTariffRate}%`,
    recommendation:
      bestOption === "Canada (with incentives)"
        ? `Source ${category.name} from Canada. With government incentives (15% tax credit), Canadian sourcing offers best value at ${formatCurrency(Math.round(canadaWithIncentives))} landed cost, plus reduced lead time risk.`
        : bestOption === "USA"
          ? `Consider USA sourcing for ${category.name}. Despite ${usaTariffRate}% tariff, the ${comparisons[1].lead_time_days}-day lead time reduces project risk vs China's ${comparisons[2].lead_time_days} days.`
          : `China offers lowest base cost for ${category.name}, but factor in ${chinaTariffRate}% tariff, ${comparisons[2].lead_time_days}-day lead time, and tariff volatility.`,
  };
}

function generateMockLeadTimes(
  categories: MaterialCategory[],
  startDate: string,
) {
  const leadTimesByCategory: Record<
    string,
    { base: number; buffer: number; critical: boolean }
  > = {
    flooring: { base: 21, buffer: 7, critical: true },
    doors: { base: 28, buffer: 10, critical: true },
    cabinets: { base: 42, buffer: 14, critical: true },
    tubs_showers: { base: 35, buffer: 10, critical: false },
    fixtures: { base: 14, buffer: 5, critical: false },
    countertops: { base: 28, buffer: 7, critical: true },
    appliances: { base: 14, buffer: 7, critical: false },
    windows: { base: 35, buffer: 14, critical: true },
    trim: { base: 7, buffer: 3, critical: false },
  };

  const projectStart = new Date(startDate);

  return {
    schedule: categories.map((cat) => {
      const config = leadTimesByCategory[cat.id] || {
        base: 21,
        buffer: 7,
        critical: false,
      };

      const arrivalDate = new Date(projectStart);
      // Critical items need to arrive before project starts
      if (config.critical) {
        arrivalDate.setDate(arrivalDate.getDate() - 7);
      } else {
        arrivalDate.setDate(arrivalDate.getDate() + 14);
      }

      const orderByDate = new Date(arrivalDate);
      orderByDate.setDate(orderByDate.getDate() - config.base - config.buffer);

      return {
        material: cat.name,
        order_by: orderByDate.toISOString().split("T")[0],
        expected_arrival: arrivalDate.toISOString().split("T")[0],
        buffer_days: config.buffer,
        is_critical: config.critical,
      };
    }),
  };
}

// Lead Time Schedule item type
interface LeadTimeItem {
  material: string;
  order_by: string;
  expected_arrival: string;
  buffer_days: number;
  is_critical?: boolean;
}

// Export Functions
function exportToPDF(
  selectedCategories: MaterialCategory[],
  comparisonResult:
    | ReturnType<typeof generateMockComparison>
    | null
    | undefined,
  leadTimesData: LeadTimeItem[],
  tariffs: TariffRates,
  projectBudget: number,
  projectDays: number,
  orResults: Record<string, Record<string, unknown>>,
) {
  // Create a new window for printing
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow pop-ups to export the PDF report.");
    return;
  }

  // Build the document using DOM manipulation
  const doc = printWindow.document;

  // Create and append style element
  const style = doc.createElement("style");
  style.textContent = `
    body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
    h1 { color: #0d9488; border-bottom: 2px solid #0d9488; padding-bottom: 10px; }
    h2 { color: #374151; margin-top: 30px; }
    h3 { color: #6b7280; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { border: 1px solid #e5e7eb; padding: 10px; text-align: left; }
    th { background-color: #f3f4f6; font-weight: 600; }
    .summary-box { background: #f0fdfa; border: 1px solid #99f6e4; padding: 15px; border-radius: 8px; margin: 15px 0; }
    .warning-box { background: #fffbeb; border: 1px solid #fde68a; padding: 15px; border-radius: 8px; margin: 15px 0; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #9ca3af; }
    @media print { body { padding: 0; } }
  `;
  doc.head.appendChild(style);

  // Set document title
  doc.title = "Feasibility Study Report";

  // Create main content container
  const container = doc.createElement("div");

  // Helper function to create elements with text
  const createElement = (
    tag: string,
    text?: string,
    className?: string,
  ): HTMLElement => {
    const el = doc.createElement(tag);
    if (text) el.textContent = text;
    if (className) el.className = className;
    return el;
  };

  // Helper to create table
  const createTable = (
    headers: string[],
    rows: string[][],
  ): HTMLTableElement => {
    const table = doc.createElement("table");
    const thead = doc.createElement("thead");
    const headerRow = doc.createElement("tr");
    headers.forEach((h) => {
      const th = doc.createElement("th");
      th.textContent = h;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = doc.createElement("tbody");
    rows.forEach((row) => {
      const tr = doc.createElement("tr");
      row.forEach((cell) => {
        const td = doc.createElement("td");
        td.textContent = cell;
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    return table;
  };

  // Title
  container.appendChild(
    createElement("h1", "Finish Materials Feasibility Study"),
  );
  container.appendChild(
    createElement(
      "p",
      `Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
    ),
  );

  // Project Parameters
  const summaryBox = createElement("div", undefined, "summary-box");
  summaryBox.appendChild(createElement("h3", "Project Parameters"));
  summaryBox.appendChild(
    createElement("p", `Budget: $${projectBudget.toLocaleString()} CAD`),
  );
  summaryBox.appendChild(createElement("p", `Timeline: ${projectDays} days`));
  summaryBox.appendChild(
    createElement(
      "p",
      `Selected Materials: ${selectedCategories.map((c) => c.name).join(", ") || "None"}`,
    ),
  );
  container.appendChild(summaryBox);

  // Tariff Rates
  container.appendChild(createElement("h2", "Tariff Rates (January 2026)"));
  container.appendChild(
    createTable(
      ["USA to Canada", "Rate"],
      [
        ["Steel", `${tariffs.usa.steel}%`],
        ["Aluminum", `${tariffs.usa.aluminum}%`],
        ["Lumber", `${tariffs.usa.lumber}%`],
        ["Cabinets", `${tariffs.usa.cabinets}%`],
        ["General", `${tariffs.usa.general}%`],
        ["Shipping", `${tariffs.usa.shipping}%`],
      ],
    ),
  );
  container.appendChild(
    createTable(
      ["China to Canada", "Rate"],
      [
        ["General", `${tariffs.china.general}%`],
        ["Steel", `${tariffs.china.steel}%`],
        ["Shipping", `${tariffs.china.shipping}%`],
        ["Customs Brokerage", `${tariffs.china.customsBrokerage}%`],
      ],
    ),
  );

  // Price Comparison
  if (comparisonResult?.comparisons) {
    container.appendChild(createElement("h2", "Price Comparison by Source"));
    container.appendChild(
      createTable(
        [
          "Country",
          "Unit Cost",
          "Base Cost",
          "Tariff",
          "Rate",
          "Shipping",
          "Total Landed",
          "Lead Time",
        ],
        comparisonResult.comparisons.map((c) => [
          c.country,
          `$${c.unit_cost.toLocaleString()}`,
          `$${c.base_cost.toLocaleString()}`,
          `$${c.tariff_cost.toLocaleString()}`,
          `${c.tariff_rate}%`,
          `$${c.shipping_cost.toLocaleString()}`,
          `$${c.total_landed_cost.toLocaleString()}`,
          `${c.lead_time_days} days`,
        ]),
      ),
    );
    if (comparisonResult.recommendation) {
      const recBox = createElement("div", undefined, "summary-box");
      recBox.appendChild(createElement("strong", "Recommendation: "));
      recBox.appendChild(doc.createTextNode(comparisonResult.recommendation));
      container.appendChild(recBox);
    }
  }

  // Lead Times
  if (leadTimesData.length > 0) {
    container.appendChild(createElement("h2", "Lead Time Schedule"));
    container.appendChild(
      createTable(
        ["Material", "Order By", "Expected Arrival", "Buffer Days", "Critical"],
        leadTimesData.map((item) => [
          item.material,
          item.order_by,
          item.expected_arrival,
          item.buffer_days.toString(),
          item.is_critical ? "Yes" : "No",
        ]),
      ),
    );
  }

  // Monte Carlo Results
  const mcData = orResults["monte-carlo"] as {
    results_by_source?: Record<
      string,
      { mean: number; p5: number; p95: number; prob_under_budget: number }
    >;
  };
  if (mcData?.results_by_source) {
    container.appendChild(
      createElement("h2", "Cost Risk Analysis (Monte Carlo)"),
    );
    container.appendChild(
      createTable(
        [
          "Country",
          "Best Case (5%)",
          "Average",
          "Worst Case (95%)",
          "Prob Under Budget",
        ],
        Object.entries(mcData.results_by_source).map(([country, data]) => [
          country.charAt(0).toUpperCase() + country.slice(1),
          `$${data.p5.toLocaleString()}`,
          `$${data.mean.toLocaleString()}`,
          `$${data.p95.toLocaleString()}`,
          `${data.prob_under_budget}%`,
        ]),
      ),
    );
  }

  // Decision Tree Results
  const dtData = orResults["decision-tree"] as {
    analysis?: Array<{ source: string; emv: number }>;
  };
  if (dtData?.analysis) {
    container.appendChild(createElement("h2", "Sourcing Decision Analysis"));
    container.appendChild(
      createTable(
        ["Source", "Expected Monetary Value (EMV)"],
        dtData.analysis.map((opt) => [
          opt.source.charAt(0).toUpperCase() + opt.source.slice(1),
          `$${opt.emv.toLocaleString()}`,
        ]),
      ),
    );
  }

  // Risk Assessment
  const warningBox = createElement("div", undefined, "warning-box");
  warningBox.appendChild(createElement("h3", "Risk Assessment"));
  warningBox.appendChild(
    createElement(
      "p",
      "Late Arrival Penalty: $2,500/day (contractor standby and financing costs)",
    ),
  );
  warningBox.appendChild(
    createElement(
      "p",
      "Lost Sales Cost: $5,000/day (if income-generating property)",
    ),
  );
  container.appendChild(warningBox);

  // Footer
  const footer = createElement("div", undefined, "footer");
  footer.appendChild(
    createElement(
      "p",
      "Generated by Builder Buddy - Finish Materials Feasibility Study Tool",
    ),
  );
  footer.appendChild(
    createElement(
      "p",
      "Tariff rates sourced from Canada.ca, Blake, Cassels & Graydon LLP (January 2026)",
    ),
  );
  container.appendChild(footer);

  // Append container to body
  doc.body.appendChild(container);

  // Trigger print after a short delay to ensure rendering
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 250);
}

function exportToCSV(
  selectedCategories: MaterialCategory[],
  comparisonResult:
    | ReturnType<typeof generateMockComparison>
    | null
    | undefined,
  leadTimesData: LeadTimeItem[],
  tariffs: TariffRates,
  projectBudget: number,
  projectDays: number,
  orResults: Record<string, Record<string, unknown>>,
) {
  const rows: string[][] = [];

  // Header section
  rows.push(["Feasibility Study Export"]);
  rows.push(["Generated", new Date().toISOString()]);
  rows.push([]);

  // Project parameters
  rows.push(["PROJECT PARAMETERS"]);
  rows.push(["Budget (CAD)", projectBudget.toString()]);
  rows.push(["Timeline (days)", projectDays.toString()]);
  rows.push([
    "Selected Materials",
    selectedCategories.map((c) => c.name).join("; "),
  ]);
  rows.push([]);

  // Tariff rates
  rows.push(["TARIFF RATES (January 2026)"]);
  rows.push(["USA Tariffs"]);
  rows.push(["Category", "Rate (%)"]);
  rows.push(["Steel", tariffs.usa.steel.toString()]);
  rows.push(["Aluminum", tariffs.usa.aluminum.toString()]);
  rows.push(["Lumber", tariffs.usa.lumber.toString()]);
  rows.push(["Cabinets", tariffs.usa.cabinets.toString()]);
  rows.push(["General", tariffs.usa.general.toString()]);
  rows.push(["Shipping", tariffs.usa.shipping.toString()]);
  rows.push([]);
  rows.push(["China Tariffs"]);
  rows.push(["Category", "Rate (%)"]);
  rows.push(["General", tariffs.china.general.toString()]);
  rows.push(["Steel", tariffs.china.steel.toString()]);
  rows.push(["Shipping", tariffs.china.shipping.toString()]);
  rows.push(["Customs Brokerage", tariffs.china.customsBrokerage.toString()]);
  rows.push([]);

  // Price comparisons
  if (comparisonResult?.comparisons) {
    rows.push(["PRICE COMPARISON BY SOURCE"]);
    rows.push([
      "Country",
      "Unit Cost",
      "Base Cost",
      "Tariff Cost",
      "Tariff Rate (%)",
      "Shipping Cost",
      "Total Landed Cost",
      "Lead Time (days)",
    ]);
    comparisonResult.comparisons.forEach((c) => {
      rows.push([
        c.country,
        c.unit_cost.toString(),
        c.base_cost.toString(),
        c.tariff_cost.toString(),
        c.tariff_rate.toString(),
        c.shipping_cost.toString(),
        c.total_landed_cost.toString(),
        c.lead_time_days.toString(),
      ]);
    });
    if (comparisonResult.recommendation) {
      rows.push([]);
      rows.push(["Recommendation", comparisonResult.recommendation]);
    }
    rows.push([]);
  }

  // Lead times
  if (leadTimesData.length > 0) {
    rows.push(["LEAD TIME SCHEDULE"]);
    rows.push([
      "Material",
      "Order By",
      "Expected Arrival",
      "Buffer Days",
      "Is Critical",
    ]);
    leadTimesData.forEach((item) => {
      rows.push([
        item.material,
        item.order_by,
        item.expected_arrival,
        item.buffer_days.toString(),
        item.is_critical ? "Yes" : "No",
      ]);
    });
    rows.push([]);
  }

  // Monte Carlo results
  const monteCarloData = orResults["monte-carlo"] as {
    results_by_source?: Record<
      string,
      { mean: number; p5: number; p95: number; prob_under_budget: number }
    >;
  };
  if (monteCarloData?.results_by_source) {
    rows.push(["COST RISK ANALYSIS (MONTE CARLO)"]);
    rows.push([
      "Country",
      "Best Case (5%)",
      "Average",
      "Worst Case (95%)",
      "Probability Under Budget (%)",
    ]);
    Object.entries(monteCarloData.results_by_source).forEach(
      ([country, data]) => {
        rows.push([
          country,
          data.p5.toString(),
          data.mean.toString(),
          data.p95.toString(),
          data.prob_under_budget.toString(),
        ]);
      },
    );
    rows.push([]);
  }

  // Decision tree results
  const decisionTreeData = orResults["decision-tree"] as {
    analysis?: Array<{
      source: string;
      emv: number;
      best_case: number;
      worst_case: number;
    }>;
    recommendations?: { lowest_expected_cost: string; risk_adjusted: string };
  };
  if (decisionTreeData?.analysis) {
    rows.push(["SOURCING DECISION ANALYSIS"]);
    rows.push([
      "Source",
      "Expected Monetary Value (EMV)",
      "Best Case",
      "Worst Case",
    ]);
    decisionTreeData.analysis.forEach((opt) => {
      rows.push([
        opt.source,
        opt.emv.toString(),
        opt.best_case.toString(),
        opt.worst_case.toString(),
      ]);
    });
    if (decisionTreeData.recommendations) {
      rows.push([]);
      rows.push([
        "Recommended (Lowest Cost)",
        decisionTreeData.recommendations.lowest_expected_cost,
      ]);
      rows.push([
        "Recommended (Risk Adjusted)",
        decisionTreeData.recommendations.risk_adjusted,
      ]);
    }
    rows.push([]);
  }

  // JIT results
  const jitData = orResults.jit as {
    schedule?: Array<{
      material: string;
      jit_order_date: string;
      expected_arrival: string;
      buffer_days: number;
      need_date: string;
    }>;
    total_storage_saved?: number;
    risk_level?: string;
  };
  if (jitData?.schedule) {
    rows.push(["JIT ORDER SCHEDULE"]);
    rows.push([
      "Material",
      "JIT Order Date",
      "Expected Arrival",
      "Need Date",
      "Buffer Days",
    ]);
    jitData.schedule.forEach((item) => {
      rows.push([
        item.material,
        item.jit_order_date,
        item.expected_arrival,
        item.need_date,
        item.buffer_days.toString(),
      ]);
    });
    if (jitData.total_storage_saved !== undefined) {
      rows.push([]);
      rows.push([
        "Total Storage Saved",
        jitData.total_storage_saved.toString(),
      ]);
    }
    if (jitData.risk_level) {
      rows.push(["Risk Level", jitData.risk_level]);
    }
    rows.push([]);
  }

  // Risk assessment
  rows.push(["RISK ASSESSMENT"]);
  rows.push(["Late Arrival Penalty (per day)", "2500"]);
  rows.push(["Lost Sales Cost (per day)", "5000"]);

  // Convert to CSV string
  const csvContent = rows
    .map((row) =>
      row
        .map((cell) => {
          // Escape quotes and wrap in quotes if contains comma, newline, or quote
          const escaped = cell.replace(/"/g, '""');
          if (
            escaped.includes(",") ||
            escaped.includes("\n") ||
            escaped.includes('"')
          ) {
            return `"${escaped}"`;
          }
          return escaped;
        })
        .join(","),
    )
    .join("\n");

  // Download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `feasibility-study-${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// OR Technique Result Display Components
function ORTechniqueCard({
  technique,
  isSelected,
  onClick,
  result,
  isLoading,
}: {
  technique: ORTechnique;
  isSelected: boolean;
  onClick: () => void;
  result?: Record<string, unknown>;
  isLoading?: boolean;
}) {
  const Icon = technique.icon;
  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-lg border text-left transition-all w-full ${
        isSelected
          ? "border-teal-500 bg-teal-50 ring-2 ring-teal-200"
          : "border-gray-200 hover:border-teal-300 hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div
            className={`p-2 rounded-lg ${isSelected ? "bg-teal-100" : "bg-gray-100"}`}
          >
            <Icon
              className={`h-5 w-5 ${isSelected ? "text-teal-600" : "text-gray-500"}`}
            />
          </div>
          <span
            className={`font-medium ${isSelected ? "text-teal-900" : "text-gray-700"}`}
          >
            {technique.shortName}
          </span>
        </div>
        {isLoading && (
          <Loader2 className="h-4 w-4 animate-spin text-teal-500" />
        )}
        {result && !isLoading && (
          <CheckCircle className="h-4 w-4 text-green-500" />
        )}
      </div>
      <p className="text-sm font-medium text-gray-900 mb-1">
        {technique.userQuestion}
      </p>
      <p className="text-xs text-gray-500">{technique.description}</p>
    </button>
  );
}

export function FeasibilityStudyPage() {
  const [categories, setCategories] = useState(materialCategories);
  const [projectBudget, setProjectBudget] = useState(500000);
  const [projectDays, setProjectDays] = useState(180);
  const [projectStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date.toISOString().split("T")[0];
  });

  // OR Technique state
  const [selectedTechnique, setSelectedTechnique] = useState<string | null>(
    null,
  );
  const [orResults, setOrResults] = useState<
    Record<string, Record<string, unknown>>
  >({});
  const [showORDetails, setShowORDetails] = useState(false);

  // OR Parameter state (with defaults) - Only for the 4 core techniques
  const [pertParams, setPertParams] = useState({
    optimistic: 60,
    most_likely: 90,
    pessimistic: 150,
  });
  const [monteCarloParams, setMonteCarloParams] = useState({
    budget: 100000,
    num_simulations: 10000,
  });
  const [jitParams, setJitParams] = useState({
    storage_cost_per_day: 5.0,
  });

  // Fetch defaults on mount (used to populate initial parameters)
  useQuery({
    queryKey: ["feasibility-defaults"],
    queryFn: fetchFeasibilityDefaults,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Adjustable tariff rates (January 2026 defaults from Canada.ca)
  const [tariffs, setTariffs] = useState<TariffRates>({
    usa: {
      steel: 25, // Canada's counter-tariff on US steel
      aluminum: 25, // Canada's counter-tariff on US aluminum
      lumber: 15, // US softwood lumber tariff + anti-dumping
      cabinets: 50, // US tariff on kitchen cabinets (Jan 1, 2026)
      general: 0, // Most goods exempt under CUSMA
      shipping: 5, // Cross-border shipping ~5%
    },
    china: {
      general: 30, // 10% + 10% + universal tariff
      steel: 50, // Higher rate for steel products
      shipping: 12, // Ocean freight + handling
      customsBrokerage: 3, // Customs broker fees
    },
  });
  const [showTariffSettings, setShowTariffSettings] = useState(false);

  const selectedCategories = categories.filter((c) => c.selected);

  const toggleCategory = (id: string) => {
    setCategories(
      categories.map((c) =>
        c.id === id ? { ...c, selected: !c.selected } : c,
      ),
    );
  };

  // Full OR analysis mutation - calls real API
  const fullAnalysisMutation = useMutation({
    mutationFn: async () => {
      return runFullAnalysis(projectBudget, projectDays);
    },
    onSuccess: (data) => {
      // Store results from each OR technique
      setOrResults({
        eoq: data.eoq || {},
        pert: data.pert || {},
        cpm: data.cpm || {},
        "monte-carlo": data.monte_carlo || {},
        "linear-program": data.linear_program || {},
        "decision-tree": data.decision_tree || {},
        "network-flow": data.network_flow || {},
        jit: data.jit || {},
      });
    },
  });

  // Individual OR technique mutations - 4 core techniques
  const monteCarloMutation = useMutation({
    mutationFn: async () => runMonteCarloAnalysis(monteCarloParams),
    onSuccess: (data) =>
      setOrResults((prev) => ({ ...prev, "monte-carlo": data })),
  });

  const decisionTreeMutation = useMutation({
    mutationFn: async () => runDecisionTreeAnalysis(),
    onSuccess: (data) =>
      setOrResults((prev) => ({ ...prev, "decision-tree": data })),
  });

  const jitMutation = useMutation({
    mutationFn: async () =>
      runJITAnalysis({ ...jitParams, project_start: projectStartDate }),
    onSuccess: (data) => setOrResults((prev) => ({ ...prev, jit: data })),
  });

  // Combined timeline mutation (PERT + CPM)
  const timelineMutation = useMutation({
    mutationFn: async () => {
      const [pertResult, cpmResult] = await Promise.all([
        runPERTAnalysis(pertParams),
        runCPMAnalysis(),
      ]);
      return { pert: pertResult, cpm: cpmResult };
    },
    onSuccess: (data) =>
      setOrResults((prev) => ({
        ...prev,
        timeline: data,
        pert: data.pert,
        cpm: data.cpm,
      })),
  });

  // Map technique ID to mutation - Simplified to 4 core techniques
  const techniqueMutations: Record<string, typeof monteCarloMutation> = {
    "monte-carlo": monteCarloMutation,
    "decision-tree": decisionTreeMutation,
    jit: jitMutation,
    timeline: timelineMutation as typeof monteCarloMutation,
  };

  // Material comparison mutation - uses mock data generator with tariffs (kept for cost display)
  const comparisonMutation = useMutation({
    mutationFn: async () => {
      if (selectedCategories.length === 0) return null;
      // Uses mock generator since API doesn't have material-specific pricing yet
      await new Promise((resolve) => setTimeout(resolve, 300));
      const category = selectedCategories[0];
      return generateMockComparison(category, projectBudget, tariffs);
    },
  });

  // Lead times mutation - uses JIT analysis from API
  const leadTimesMutation = useMutation({
    mutationFn: async () => {
      if (selectedCategories.length === 0) return null;
      // Use JIT API for lead time calculation
      const jitResult = await runJITAnalysis({
        project_start: projectStartDate,
        storage_cost_per_day: jitParams.storage_cost_per_day,
      });
      setOrResults((prev) => ({ ...prev, jit: jitResult }));
      // Convert JIT schedule to lead times format
      return {
        schedule:
          jitResult.schedule?.map(
            (item: {
              material: string;
              order_date: string;
              expected_arrival: string;
              buffer_days: number;
            }) => ({
              material: item.material,
              order_by: item.order_date,
              expected_arrival: item.expected_arrival,
              buffer_days: item.buffer_days,
              is_critical: item.buffer_days <= 3,
            }),
          ) ||
          generateMockLeadTimes(selectedCategories, projectStartDate).schedule,
      };
    },
  });

  // Run analysis - now calls real OR APIs
  const runAnalysis = () => {
    // Run full OR analysis
    fullAnalysisMutation.mutate();
    // Also run comparison for UI display
    comparisonMutation.mutate();
    leadTimesMutation.mutate();
    setShowORDetails(true);
  };

  // Run specific OR technique
  const runTechnique = (techniqueId: string) => {
    setSelectedTechnique(techniqueId);
    techniqueMutations[techniqueId]?.mutate();
  };

  const isAnalyzing =
    comparisonMutation.isPending ||
    leadTimesMutation.isPending ||
    fullAnalysisMutation.isPending;
  const hasResults =
    comparisonMutation.data ||
    leadTimesMutation.data ||
    Object.keys(orResults).length > 0;

  // Check if any OR mutation is loading
  const orIsLoading = (techniqueId: string) =>
    techniqueMutations[techniqueId]?.isPending || false;

  // Use fallback incentives (API not available)
  const incentives: Incentive[] = fallbackIncentives;

  // Get comparison data
  const comparisonResult = comparisonMutation.data;
  const comparisonPrices = comparisonResult?.comparisons
    ? comparisonResult.comparisons.map((c) => ({
        label: c.country,
        amount: c.total_landed_cost,
        isRecommended: c.country === "Canada",
      }))
    : null;

  // Get lead times data
  const leadTimesResult = leadTimesMutation.data;
  const leadTimesData: LeadTimeItem[] = leadTimesResult?.schedule || [];

  // FAQ data for SEO
  const feasibilityFAQs = [
    {
      question: "Is it worth building in Calgary right now?",
      answer:
        "Calgary remains one of Canada's most affordable major cities for construction. With 2026 construction costs averaging $200-350/sqft for residential and strong rental demand, new builds can achieve 6-10% ROI. Our feasibility calculator helps you analyze specific project economics with current Calgary market data.",
    },
    {
      question:
        "What government incentives are available for Calgary construction?",
      answer:
        "Calgary and Alberta offer several construction incentives: Alberta Manufacturing Investment Tax Credit (10% non-refundable), SR&ED Tax Credit (15-35% for innovative methods), Green Building Permit Fee Reductions (up to 50%), and the Canada Greener Homes Grant for energy efficiency upgrades. Our calculator automatically factors in applicable incentives.",
    },
    {
      question: "How long does construction take in Calgary?",
      answer:
        "Typical Calgary construction timelines: Single-family home (6-12 months), basement development (8-12 weeks), secondary suite (3-4 months), multi-family (12-24 months). Weather impacts winter scheduling. Our PERT analysis provides confidence intervals for your specific project timeline.",
    },
    {
      question: "What affects construction costs in Alberta?",
      answer:
        "Key cost factors include: material sourcing (Canadian vs imported with tariffs), labour market conditions, site conditions and excavation requirements, permit fees and development charges, energy efficiency requirements, and seasonal timing. Our calculator analyzes all these factors with current Calgary data.",
    },
    {
      question: "How do tariffs affect building material costs in Canada?",
      answer:
        "2026 tariff impacts: US steel/aluminum (25% counter-tariff), US lumber (15% + anti-dumping), US kitchen cabinets (50%), China general goods (30%). Our calculator compares total landed costs including tariffs, shipping, and lead times to recommend optimal sourcing strategies.",
    },
    {
      question: "What is a construction feasibility study?",
      answer:
        "A feasibility study analyzes whether a construction project makes financial sense. It examines land costs, construction costs, financing, timeline, ROI potential, and risks. Our Calgary-specific calculator provides professional-grade analysis using operations research optimization techniques like Monte Carlo simulation and decision tree analysis.",
    },
  ];

  // State for collapsible sections
  const [showInfoSection, setShowInfoSection] = useState(true);
  const [showFAQ, setShowFAQ] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  return (
    <>
      <SEO
        faqs={feasibilityFAQs}
        breadcrumbs={[
          { name: "Home", url: "https://builderbuddy.ca/" },
          {
            name: "Feasibility Calculator",
            url: "https://builderbuddy.ca/feasibility",
          },
        ]}
      />
      <div className="space-y-6">
        {/* Enhanced Header with Credibility Signals */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant="warning"
                  size="sm"
                  className="bg-amber-400 text-amber-900"
                >
                  Calgary Market Data
                </Badge>
                <Badge
                  variant="default"
                  size="sm"
                  className="bg-white/20 text-white border-white/30"
                >
                  Free Tool
                </Badge>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                <TrendingUp className="h-8 w-8" />
                Calgary Development Feasibility Calculator
              </h1>
              <p className="text-teal-100 mt-2 text-lg max-w-2xl">
                Make informed investment decisions with real Calgary market
                data, government incentives, and operations research
                optimization.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                <span className="flex items-center gap-1">
                  <Shield className="h-4 w-4" />
                  2026 Tariff Rates
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  Calgary-Specific Data
                </span>
                <span className="flex items-center gap-1">
                  <Award className="h-4 w-4" />
                  Professional-Grade Analysis
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section - What is a Feasibility Study */}
        <Card variant="outlined" className="border-blue-200 bg-blue-50/50">
          <button
            onClick={() => setShowInfoSection(!showInfoSection)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-blue-900">
                What is a Development Feasibility Study?
              </span>
            </div>
            {showInfoSection ? (
              <ChevronUp className="h-5 w-5 text-blue-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-blue-600" />
            )}
          </button>

          {showInfoSection && (
            <div className="mt-4 pt-4 border-t border-blue-200 space-y-4">
              <p className="text-gray-700">
                A <strong>feasibility study</strong> analyzes whether a
                construction or development project makes financial sense before
                you commit significant resources. It helps you understand costs,
                timelines, risks, and potential returns.
              </p>

              <div className="grid md:grid-cols-3 gap-4">
                {/* Who needs it */}
                <div className="p-4 bg-white rounded-lg border border-blue-100">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    Who Needs This?
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>- Real estate developers</li>
                    <li>- Property investors</li>
                    <li>- Homeowners planning major renovations</li>
                    <li>- Builders & contractors</li>
                    <li>- Anyone considering new construction</li>
                  </ul>
                </div>

                {/* What decisions it helps with */}
                <div className="p-4 bg-white rounded-lg border border-blue-100">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                    <HelpCircle className="h-5 w-5 text-blue-600" />
                    Decisions It Helps With
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>- Build vs. buy existing property</li>
                    <li>- Renovation vs. new construction</li>
                    <li>- Material sourcing strategy</li>
                    <li>- Project timing optimization</li>
                    <li>- Budget allocation</li>
                  </ul>
                </div>

                {/* Why Calgary-specific */}
                <div className="p-4 bg-white rounded-lg border border-blue-100">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    Why Calgary-Specific Data?
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>- Local material supplier pricing</li>
                    <li>- Alberta government incentives</li>
                    <li>- Calgary labour market rates</li>
                    <li>- Regional lead time estimates</li>
                    <li>- Local permit & development fees</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Use Case Examples */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card
            variant="outlined"
            className="p-4 hover:border-teal-300 transition-colors cursor-pointer"
            onClick={() => {
              setProjectBudget(600000);
              setProjectDays(270);
            }}
          >
            <Home className="h-6 w-6 text-teal-600 mb-2" />
            <h4 className="font-semibold text-gray-900 text-sm">
              New Home Build
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Single-family in Calgary suburbs
            </p>
            <p className="text-xs text-teal-600 mt-2">
              ~$600K budget, 9 months
            </p>
          </Card>

          <Card
            variant="outlined"
            className="p-4 hover:border-teal-300 transition-colors cursor-pointer"
            onClick={() => {
              setProjectBudget(2500000);
              setProjectDays(540);
            }}
          >
            <Building2 className="h-6 w-6 text-teal-600 mb-2" />
            <h4 className="font-semibold text-gray-900 text-sm">
              Multi-Family Development
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              4-plex or townhouse project
            </p>
            <p className="text-xs text-teal-600 mt-2">
              ~$2.5M budget, 18 months
            </p>
          </Card>

          <Card
            variant="outlined"
            className="p-4 hover:border-teal-300 transition-colors cursor-pointer"
            onClick={() => {
              setProjectBudget(150000);
              setProjectDays(120);
            }}
          >
            <Home className="h-6 w-6 text-teal-600 mb-2" />
            <h4 className="font-semibold text-gray-900 text-sm">
              Major Renovation
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Kitchen, bath, basement
            </p>
            <p className="text-xs text-teal-600 mt-2">
              ~$150K budget, 4 months
            </p>
          </Card>

          <Card
            variant="outlined"
            className="p-4 hover:border-teal-300 transition-colors cursor-pointer"
            onClick={() => {
              setProjectBudget(80000);
              setProjectDays(90);
            }}
          >
            <Users className="h-6 w-6 text-teal-600 mb-2" />
            <h4 className="font-semibold text-gray-900 text-sm">
              Secondary Suite
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Basement or garage suite
            </p>
            <p className="text-xs text-teal-600 mt-2">~$80K budget, 3 months</p>
          </Card>
        </div>

        {/* OR Techniques Interactive Panel */}
        <Card variant="outlined" className="bg-blue-50 border-blue-200">
          <button
            onClick={() => setShowORDetails(!showORDetails)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium text-blue-900">
                  Operations Research Optimization Techniques
                </p>
                <p className="text-sm text-blue-700">
                  Click to explore individual techniques with adjustable
                  parameters
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {fullAnalysisMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              )}
              {Object.keys(orResults).length > 0 && (
                <Badge variant="success" size="sm">
                  {Object.keys(orResults).length} Complete
                </Badge>
              )}
              {showORDetails ? (
                <ChevronUp className="h-5 w-5 text-blue-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-blue-600" />
              )}
            </div>
          </button>

          {showORDetails && (
            <div className="mt-4 pt-4 border-t border-blue-200 space-y-4">
              {/* Technique Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {orTechniques.map((tech) => (
                  <ORTechniqueCard
                    key={tech.id}
                    technique={tech}
                    isSelected={selectedTechnique === tech.id}
                    onClick={() => runTechnique(tech.id)}
                    result={orResults[tech.id]}
                    isLoading={orIsLoading(tech.id)}
                  />
                ))}
              </div>

              {/* Selected Technique Details with Explanations */}
              <AnimatePresence>
                {selectedTechnique && orResults[selectedTechnique] && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <Card variant="elevated" className="bg-white">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-900 text-lg">
                          {
                            orTechniques.find((t) => t.id === selectedTechnique)
                              ?.name
                          }
                        </h4>
                        <Badge variant="success" size="sm">
                          Calculated
                        </Badge>
                      </div>

                      {/* 1. MONTE CARLO - Cost Risk Analysis */}
                      {selectedTechnique === "monte-carlo" && (
                        <div className="space-y-4">
                          {/* Explanation */}
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-800">
                              <strong>What this tells you:</strong> We ran{" "}
                              {monteCarloParams.num_simulations.toLocaleString()}{" "}
                              simulations with random cost variations to show
                              you the range of possible outcomes. This helps you
                              understand the risk of going over budget when
                              sourcing from different countries.
                            </p>
                          </div>

                          {/* Parameters */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Your Budget ($)
                              </label>
                              <input
                                type="number"
                                value={monteCarloParams.budget}
                                onChange={(e) =>
                                  setMonteCarloParams({
                                    ...monteCarloParams,
                                    budget: Number(e.target.value),
                                  })
                                }
                                className="w-full px-3 py-2 text-sm border rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Simulations
                              </label>
                              <input
                                type="number"
                                value={monteCarloParams.num_simulations}
                                onChange={(e) =>
                                  setMonteCarloParams({
                                    ...monteCarloParams,
                                    num_simulations: Number(e.target.value),
                                  })
                                }
                                className="w-full px-3 py-2 text-sm border rounded-lg"
                              />
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => monteCarloMutation.mutate()}
                            disabled={monteCarloMutation.isPending}
                          >
                            {monteCarloMutation.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : null}
                            Recalculate Risk
                          </Button>

                          {/* Results by Country */}
                          {(
                            orResults["monte-carlo"] as {
                              results_by_source?: Record<
                                string,
                                {
                                  mean: number;
                                  p5: number;
                                  p95: number;
                                  prob_under_budget: number;
                                }
                              >;
                            }
                          )?.results_by_source && (
                            <div className="space-y-3 pt-4 border-t">
                              <h5 className="font-medium text-gray-700">
                                Cost Risk by Sourcing Country
                              </h5>
                              {Object.entries(
                                (
                                  orResults["monte-carlo"] as {
                                    results_by_source: Record<
                                      string,
                                      {
                                        mean: number;
                                        p5: number;
                                        p95: number;
                                        prob_under_budget: number;
                                      }
                                    >;
                                  }
                                ).results_by_source,
                              ).map(([country, data]) => (
                                <div
                                  key={country}
                                  className={`p-4 rounded-lg border ${data.prob_under_budget > 80 ? "bg-green-50 border-green-200" : data.prob_under_budget > 50 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-semibold text-gray-900 capitalize">
                                      {country}
                                    </span>
                                    <Badge
                                      variant={
                                        data.prob_under_budget > 80
                                          ? "success"
                                          : data.prob_under_budget > 50
                                            ? "warning"
                                            : "danger"
                                      }
                                    >
                                      {data.prob_under_budget}% chance under
                                      budget
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-sm">
                                    <div>
                                      <span className="text-gray-500">
                                        Best case:
                                      </span>
                                      <span className="ml-1 font-medium">
                                        {formatCurrency(data.p5)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">
                                        Average:
                                      </span>
                                      <span className="ml-1 font-medium">
                                        {formatCurrency(data.mean)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">
                                        Worst case:
                                      </span>
                                      <span className="ml-1 font-medium">
                                        {formatCurrency(data.p95)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Bottom Line */}
                          <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                            <p className="text-sm text-teal-800">
                              <CheckCircle className="inline h-4 w-4 mr-1" />
                              <strong>Bottom line:</strong> Based on{" "}
                              {monteCarloParams.num_simulations.toLocaleString()}{" "}
                              simulations,
                              {(
                                orResults["monte-carlo"] as {
                                  recommendation?: { conservative: string };
                                }
                              )?.recommendation?.conservative === "china"
                                ? " China offers the lowest average cost, but consider the tariff volatility risk."
                                : (
                                      orResults["monte-carlo"] as {
                                        recommendation?: {
                                          conservative: string;
                                        };
                                      }
                                    )?.recommendation?.conservative === "usa"
                                  ? " USA offers a good balance of cost and reliability."
                                  : " Canada offers the most predictable costs with lowest risk of surprises."}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* 2. DECISION TREE - Sourcing Decision */}
                      {selectedTechnique === "decision-tree" && (
                        <div className="space-y-4">
                          {/* Explanation */}
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-800">
                              <strong>What this tells you:</strong> We
                              calculated the "Expected Monetary Value" (EMV) for
                              each country by weighing possible scenarios like
                              tariff increases, currency changes, and supply
                              disruptions. Lower EMV = better value considering
                              risks.
                            </p>
                          </div>

                          <Button
                            size="sm"
                            onClick={() => decisionTreeMutation.mutate()}
                            disabled={decisionTreeMutation.isPending}
                          >
                            {decisionTreeMutation.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : null}
                            Recalculate Decision
                          </Button>

                          {/* Options Comparison */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                            {(
                              (
                                orResults["decision-tree"] as {
                                  analysis?: Array<{
                                    source: string;
                                    emv: number;
                                    best_case: number;
                                    worst_case: number;
                                    scenarios: Array<{
                                      name: string;
                                      probability: number;
                                      cost: number;
                                    }>;
                                  }>;
                                }
                              )?.analysis || []
                            ).map((opt) => {
                              const isRecommended =
                                opt.source.toLowerCase() ===
                                (
                                  orResults["decision-tree"] as {
                                    recommendations?: {
                                      lowest_expected_cost: string;
                                    };
                                  }
                                )?.recommendations?.lowest_expected_cost;
                              return (
                                <div
                                  key={opt.source}
                                  className={`p-4 rounded-lg border-2 ${isRecommended ? "bg-green-50 border-green-400" : "bg-gray-50 border-gray-200"}`}
                                >
                                  {isRecommended && (
                                    <Badge
                                      variant="success"
                                      size="sm"
                                      className="mb-2"
                                    >
                                      Recommended
                                    </Badge>
                                  )}
                                  <p className="font-semibold text-lg text-gray-900 capitalize">
                                    {opt.source}
                                  </p>
                                  <p className="text-2xl font-bold text-gray-700 mt-1">
                                    {formatCurrency(opt.emv)}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Expected cost considering all risks
                                  </p>

                                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                                    <p className="text-xs font-medium text-gray-600">
                                      Scenarios considered:
                                    </p>
                                    {opt.scenarios?.map((s, i) => (
                                      <p
                                        key={i}
                                        className="text-xs text-gray-500"
                                      >
                                        • {s.name}: {formatCurrency(s.cost)} (
                                        {Math.round(s.probability * 100)}%
                                        chance)
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Bottom Line */}
                          <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                            <p className="text-sm text-teal-800">
                              <CheckCircle className="inline h-4 w-4 mr-1" />
                              <strong>Bottom line:</strong> Considering tariff
                              risks, currency fluctuations, and supply
                              disruptions,
                              <strong className="capitalize">
                                {" "}
                                {
                                  (
                                    orResults["decision-tree"] as {
                                      recommendations?: {
                                        lowest_expected_cost: string;
                                      };
                                    }
                                  )?.recommendations?.lowest_expected_cost
                                }
                              </strong>{" "}
                              offers the best expected value. However, if you
                              prefer lower risk over lowest cost, consider
                              <strong className="capitalize">
                                {" "}
                                {
                                  (
                                    orResults["decision-tree"] as {
                                      recommendations?: {
                                        risk_adjusted: string;
                                      };
                                    }
                                  )?.recommendations?.risk_adjusted
                                }
                              </strong>
                              .
                            </p>
                          </div>
                        </div>
                      )}

                      {/* 3. JIT - Order Scheduling */}
                      {selectedTechnique === "jit" && (
                        <div className="space-y-4">
                          {/* Explanation */}
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-800">
                              <strong>What this tells you:</strong> Just-in-Time
                              scheduling calculates when to order each material
                              so it arrives right when you need it - not too
                              early (avoiding storage costs) and not too late
                              (avoiding project delays). Buffer days account for
                              delivery uncertainty.
                            </p>
                          </div>

                          {/* Parameters */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Project Start Date
                              </label>
                              <input
                                type="date"
                                value={projectStartDate}
                                disabled
                                className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-100"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Storage Cost ($/unit/day)
                              </label>
                              <input
                                type="number"
                                step="0.5"
                                value={jitParams.storage_cost_per_day}
                                onChange={(e) =>
                                  setJitParams({
                                    ...jitParams,
                                    storage_cost_per_day: Number(
                                      e.target.value,
                                    ),
                                  })
                                }
                                className="w-full px-3 py-2 text-sm border rounded-lg"
                              />
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => jitMutation.mutate()}
                            disabled={jitMutation.isPending}
                          >
                            {jitMutation.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : null}
                            Recalculate Schedule
                          </Button>

                          {/* Schedule Table */}
                          {(
                            orResults.jit as {
                              schedule?: Array<{
                                material: string;
                                jit_order_date: string;
                                expected_arrival: string;
                                buffer_days: number;
                                need_date: string;
                              }>;
                            }
                          )?.schedule && (
                            <div className="overflow-x-auto pt-4 border-t">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="text-left p-3 font-medium text-gray-700">
                                      Material
                                    </th>
                                    <th className="text-left p-3 font-medium text-gray-700">
                                      Order By
                                    </th>
                                    <th className="text-left p-3 font-medium text-gray-700">
                                      Arrives
                                    </th>
                                    <th className="text-left p-3 font-medium text-gray-700">
                                      Needed
                                    </th>
                                    <th className="text-center p-3 font-medium text-gray-700">
                                      Buffer
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {(
                                    orResults.jit as {
                                      schedule: Array<{
                                        material: string;
                                        jit_order_date: string;
                                        expected_arrival: string;
                                        buffer_days: number;
                                        need_date: string;
                                      }>;
                                    }
                                  ).schedule.map((item, i) => (
                                    <tr
                                      key={i}
                                      className={
                                        item.buffer_days <= 2
                                          ? "bg-amber-50"
                                          : ""
                                      }
                                    >
                                      <td className="p-3 font-medium text-gray-900">
                                        {item.material}
                                      </td>
                                      <td className="p-3 text-gray-600">
                                        {item.jit_order_date}
                                      </td>
                                      <td className="p-3 text-gray-600">
                                        {item.expected_arrival}
                                      </td>
                                      <td className="p-3 text-gray-600">
                                        {item.need_date}
                                      </td>
                                      <td className="p-3 text-center">
                                        <Badge
                                          variant={
                                            item.buffer_days <= 2
                                              ? "warning"
                                              : "success"
                                          }
                                          size="sm"
                                        >
                                          {item.buffer_days} days
                                        </Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Bottom Line */}
                          <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                            <p className="text-sm text-teal-800">
                              <CheckCircle className="inline h-4 w-4 mr-1" />
                              <strong>Bottom line:</strong> Following this
                              schedule saves you{" "}
                              {formatCurrency(
                                (
                                  orResults.jit as {
                                    total_storage_saved?: number;
                                  }
                                )?.total_storage_saved || 0,
                              )}{" "}
                              in storage costs. Risk level is{" "}
                              <strong>
                                {(orResults.jit as { risk_level?: string })
                                  ?.risk_level || "N/A"}
                              </strong>{" "}
                              -
                              {(orResults.jit as { risk_level?: string })
                                ?.risk_level === "low"
                                ? " all materials have adequate buffer time."
                                : " some materials have tight delivery windows - consider ordering earlier."}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* 4. TIMELINE - Project Duration (PERT + CPM) */}
                      {selectedTechnique === "timeline" && (
                        <div className="space-y-4">
                          {/* Explanation */}
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-800">
                              <strong>What this tells you:</strong> We combined
                              PERT (time estimation with uncertainty) and CPM
                              (critical path analysis) to show you realistic
                              project duration ranges and which tasks can't be
                              delayed.
                            </p>
                          </div>

                          {/* PERT Parameters */}
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Best case (days)
                              </label>
                              <input
                                type="number"
                                value={pertParams.optimistic}
                                onChange={(e) =>
                                  setPertParams({
                                    ...pertParams,
                                    optimistic: Number(e.target.value),
                                  })
                                }
                                className="w-full px-3 py-2 text-sm border rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Most likely (days)
                              </label>
                              <input
                                type="number"
                                value={pertParams.most_likely}
                                onChange={(e) =>
                                  setPertParams({
                                    ...pertParams,
                                    most_likely: Number(e.target.value),
                                  })
                                }
                                className="w-full px-3 py-2 text-sm border rounded-lg"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">
                                Worst case (days)
                              </label>
                              <input
                                type="number"
                                value={pertParams.pessimistic}
                                onChange={(e) =>
                                  setPertParams({
                                    ...pertParams,
                                    pessimistic: Number(e.target.value),
                                  })
                                }
                                className="w-full px-3 py-2 text-sm border rounded-lg"
                              />
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => timelineMutation.mutate()}
                            disabled={timelineMutation.isPending}
                          >
                            {timelineMutation.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : null}
                            Recalculate Timeline
                          </Button>

                          {/* Duration Results */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t">
                            <div className="p-4 bg-teal-50 rounded-lg">
                              <p className="text-xs text-teal-600 mb-1">
                                Expected Duration
                              </p>
                              <p className="text-2xl font-bold text-teal-900">
                                {(
                                  orResults.pert as {
                                    expected_duration?: number;
                                  }
                                )?.expected_duration ||
                                  (
                                    orResults.timeline as {
                                      pert?: { expected_duration: number };
                                    }
                                  )?.pert?.expected_duration}{" "}
                                days
                              </p>
                            </div>
                            <div className="p-4 bg-blue-50 rounded-lg">
                              <p className="text-xs text-blue-600 mb-1">
                                Uncertainty
                              </p>
                              <p className="text-2xl font-bold text-blue-900">
                                ±
                                {(
                                  orResults.pert as {
                                    standard_deviation?: number;
                                  }
                                )?.standard_deviation ||
                                  (
                                    orResults.timeline as {
                                      pert?: { standard_deviation: number };
                                    }
                                  )?.pert?.standard_deviation}{" "}
                                days
                              </p>
                            </div>
                            <div className="p-4 bg-amber-50 rounded-lg">
                              <p className="text-xs text-amber-600 mb-1">
                                95% Confidence Range
                              </p>
                              <p className="text-lg font-bold text-amber-900">
                                {(
                                  orResults.pert as {
                                    confidence_intervals?: {
                                      "95_percent": number[];
                                    };
                                  }
                                )?.confidence_intervals?.["95_percent"]?.[0] ||
                                  (
                                    orResults.timeline as {
                                      pert?: {
                                        confidence_intervals: {
                                          "95_percent": number[];
                                        };
                                      };
                                    }
                                  )?.pert?.confidence_intervals?.[
                                    "95_percent"
                                  ]?.[0]}
                                -
                                {(
                                  orResults.pert as {
                                    confidence_intervals?: {
                                      "95_percent": number[];
                                    };
                                  }
                                )?.confidence_intervals?.["95_percent"]?.[1] ||
                                  (
                                    orResults.timeline as {
                                      pert?: {
                                        confidence_intervals: {
                                          "95_percent": number[];
                                        };
                                      };
                                    }
                                  )?.pert?.confidence_intervals?.[
                                    "95_percent"
                                  ]?.[1]}{" "}
                                days
                              </p>
                            </div>
                            <div className="p-4 bg-red-50 rounded-lg">
                              <p className="text-xs text-red-600 mb-1">
                                Critical Path
                              </p>
                              <p className="text-lg font-bold text-red-900">
                                {(
                                  orResults.cpm as { project_duration?: number }
                                )?.project_duration ||
                                  (
                                    orResults.timeline as {
                                      cpm?: { project_duration: number };
                                    }
                                  )?.cpm?.project_duration}{" "}
                                days
                              </p>
                            </div>
                          </div>

                          {/* Critical Tasks */}
                          {((
                            orResults.cpm as { critical_path_names?: string[] }
                          )?.critical_path_names ||
                            (
                              orResults.timeline as {
                                cpm?: { critical_path_names: string[] };
                              }
                            )?.cpm?.critical_path_names) && (
                            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                              <p className="text-sm font-medium text-red-800 mb-2">
                                Critical Tasks (Cannot be delayed):
                              </p>
                              <p className="text-sm text-red-700">
                                {(
                                  (
                                    orResults.cpm as {
                                      critical_path_names?: string[];
                                    }
                                  )?.critical_path_names ||
                                  (
                                    orResults.timeline as {
                                      cpm?: { critical_path_names: string[] };
                                    }
                                  )?.cpm?.critical_path_names ||
                                  []
                                ).join(" → ")}
                              </p>
                            </div>
                          )}

                          {/* Bottom Line */}
                          <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                            <p className="text-sm text-teal-800">
                              <CheckCircle className="inline h-4 w-4 mr-1" />
                              <strong>Bottom line:</strong> Your project will
                              most likely take around{" "}
                              <strong>
                                {(
                                  orResults.pert as {
                                    expected_duration?: number;
                                  }
                                )?.expected_duration ||
                                  (
                                    orResults.timeline as {
                                      pert?: { expected_duration: number };
                                    }
                                  )?.pert?.expected_duration}{" "}
                                days
                              </strong>
                              , but plan for up to{" "}
                              <strong>
                                {(
                                  orResults.pert as {
                                    confidence_intervals?: {
                                      "95_percent": number[];
                                    };
                                  }
                                )?.confidence_intervals?.["95_percent"]?.[1] ||
                                  (
                                    orResults.timeline as {
                                      pert?: {
                                        confidence_intervals: {
                                          "95_percent": number[];
                                        };
                                      };
                                    }
                                  )?.pert?.confidence_intervals?.[
                                    "95_percent"
                                  ]?.[1]}{" "}
                                days
                              </strong>{" "}
                              to be safe. Focus on keeping the critical path
                              tasks on schedule - any delay there delays the
                              entire project.
                            </p>
                          </div>
                        </div>
                      )}
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="space-y-4">
            <Card variant="elevated">
              <h2 className="font-semibold text-gray-900 mb-4">
                Project Parameters
              </h2>

              <div className="space-y-4">
                <Input
                  label="Total Budget (CAD)"
                  type="number"
                  value={projectBudget}
                  onChange={(e) => setProjectBudget(Number(e.target.value))}
                  leftIcon={<DollarSign className="h-4 w-4" />}
                />

                <Input
                  label="Project Timeline (days)"
                  type="number"
                  value={projectDays}
                  onChange={(e) => setProjectDays(Number(e.target.value))}
                  leftIcon={<Calendar className="h-4 w-4" />}
                />
              </div>
            </Card>

            {/* Tariff Settings */}
            <Card
              variant="outlined"
              className="border-amber-200 bg-amber-50/50"
            >
              <button
                onClick={() => setShowTariffSettings(!showTariffSettings)}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-amber-800">
                    Tariff Rates (Jan 2026)
                  </span>
                  <Badge variant="warning" size="sm">
                    Editable
                  </Badge>
                </div>
                {showTariffSettings ? (
                  <ChevronUp className="h-4 w-4 text-amber-600" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-amber-600" />
                )}
              </button>

              {showTariffSettings && (
                <div className="mt-4 pt-4 border-t border-amber-200 space-y-4">
                  <div className="text-xs text-amber-700">
                    Adjust tariff rates to reflect current trade policies.
                    Sources: Canada.ca, Blakes LLP
                  </div>

                  {/* USA Tariffs */}
                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-2">
                      USA → Canada Tariffs
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500">
                          Steel (%)
                        </label>
                        <input
                          type="number"
                          value={tariffs.usa.steel}
                          onChange={(e) =>
                            setTariffs({
                              ...tariffs,
                              usa: {
                                ...tariffs.usa,
                                steel: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Aluminum (%)
                        </label>
                        <input
                          type="number"
                          value={tariffs.usa.aluminum}
                          onChange={(e) =>
                            setTariffs({
                              ...tariffs,
                              usa: {
                                ...tariffs.usa,
                                aluminum: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Lumber (%)
                        </label>
                        <input
                          type="number"
                          value={tariffs.usa.lumber}
                          onChange={(e) =>
                            setTariffs({
                              ...tariffs,
                              usa: {
                                ...tariffs.usa,
                                lumber: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Cabinets (%)
                        </label>
                        <input
                          type="number"
                          value={tariffs.usa.cabinets}
                          onChange={(e) =>
                            setTariffs({
                              ...tariffs,
                              usa: {
                                ...tariffs.usa,
                                cabinets: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                    </div>
                  </div>

                  {/* China Tariffs */}
                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-2">
                      China → Canada Tariffs
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500">
                          General (%)
                        </label>
                        <input
                          type="number"
                          value={tariffs.china.general}
                          onChange={(e) =>
                            setTariffs({
                              ...tariffs,
                              china: {
                                ...tariffs.china,
                                general: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Steel (%)
                        </label>
                        <input
                          type="number"
                          value={tariffs.china.steel}
                          onChange={(e) =>
                            setTariffs({
                              ...tariffs,
                              china: {
                                ...tariffs.china,
                                steel: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Shipping (%)
                        </label>
                        <input
                          type="number"
                          value={tariffs.china.shipping}
                          onChange={(e) =>
                            setTariffs({
                              ...tariffs,
                              china: {
                                ...tariffs.china,
                                shipping: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Customs (%)
                        </label>
                        <input
                          type="number"
                          value={tariffs.china.customsBrokerage}
                          onChange={(e) =>
                            setTariffs({
                              ...tariffs,
                              china: {
                                ...tariffs.china,
                                customsBrokerage: Number(e.target.value),
                              },
                            })
                          }
                          className="w-full px-2 py-1 text-sm border rounded"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            <Card variant="elevated">
              <h2 className="font-semibold text-gray-900 mb-4">
                Select Materials
              </h2>

              <div className="grid grid-cols-3 gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => toggleCategory(category.id)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      category.selected
                        ? "border-teal-500 bg-teal-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="text-2xl block mb-1">{category.icon}</span>
                    <span className="text-xs font-medium text-gray-700">
                      {category.name}
                    </span>
                  </button>
                ))}
              </div>

              <Button
                onClick={runAnalysis}
                disabled={selectedCategories.length === 0 || isAnalyzing}
                className="w-full mt-4 bg-teal-600 hover:bg-teal-700"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Run Feasibility Analysis
                  </>
                )}
              </Button>
            </Card>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2 space-y-4">
            <AnimatePresence>
              {hasResults && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Cost Comparison */}
                  <Card variant="elevated">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Globe className="h-5 w-5 text-teal-500" />
                      Source Comparison (Landed Cost in CAD)
                    </h3>

                    {comparisonPrices ? (
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-700">
                          {selectedCategories[0]?.name || "Materials"}
                        </h4>
                        <PriceComparison
                          prices={comparisonPrices}
                          currency="CAD"
                        />
                      </div>
                    ) : comparisonMutation.isPending ? (
                      <div className="flex items-center justify-center py-8 text-gray-500">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Comparing prices...
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">
                        Select materials and run analysis to see price
                        comparison
                      </p>
                    )}

                    {comparisonResult?.recommendation && (
                      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-800">
                          <CheckCircle className="inline h-4 w-4 mr-1" />
                          <strong>Recommendation:</strong>{" "}
                          {comparisonResult.recommendation}
                        </p>
                      </div>
                    )}
                  </Card>

                  {/* Government Incentives */}
                  <Card variant="elevated">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Leaf className="h-5 w-5 text-green-500" />
                      Canadian Sourcing Incentives
                    </h3>

                    <div className="space-y-3">
                      {incentives.map((incentive, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-lg bg-gray-50 border border-gray-100"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900">
                              {incentive.name}
                            </span>
                            <Badge
                              variant={
                                incentive.type === "federal"
                                  ? "danger"
                                  : incentive.type === "provincial"
                                    ? "warning"
                                    : "info"
                              }
                              size="sm"
                            >
                              {incentive.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-green-600 font-medium">
                            {incentive.benefit}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {incentive.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Lead Time Analysis */}
                  <Card variant="elevated">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-500" />
                      Lead Time & Order Schedule
                      {leadTimesMutation.isPending && (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      )}
                    </h3>

                    {leadTimesData.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-3 font-medium text-gray-600">
                                Material
                              </th>
                              <th className="text-left p-3 font-medium text-gray-600">
                                Order By
                              </th>
                              <th className="text-left p-3 font-medium text-gray-600">
                                Expected Arrival
                              </th>
                              <th className="text-center p-3 font-medium text-gray-600">
                                Buffer
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {leadTimesData.map((item, index) => (
                              <tr key={index}>
                                <td className="p-3 font-medium text-gray-900">
                                  {item.material}
                                </td>
                                <td className="p-3 text-gray-600">
                                  {item.order_by}
                                </td>
                                <td className="p-3 text-gray-600">
                                  {item.expected_arrival}
                                </td>
                                <td className="p-3 text-center">
                                  <Badge variant="default" size="sm">
                                    {item.buffer_days} days
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : leadTimesMutation.isPending ? (
                      <div className="flex items-center justify-center py-8 text-gray-500">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Calculating lead times...
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">
                        Run analysis to see lead time schedule
                      </p>
                    )}
                  </Card>

                  {/* Risk & Costs */}
                  <Card variant="elevated">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Risk Assessment & Lost Opportunity Costs
                    </h3>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <p className="font-medium text-red-900">
                          Late Arrival Penalty
                        </p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                          {formatCurrency(2500)}/day
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                          Based on contractor standby and financing costs
                        </p>
                      </div>

                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="font-medium text-amber-900">
                          Lost Sales Cost
                        </p>
                        <p className="text-2xl font-bold text-amber-600 mt-1">
                          {formatCurrency(5000)}/day
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          If project is income-generating property
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Export */}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (
                          !comparisonResult &&
                          leadTimesData.length === 0 &&
                          Object.keys(orResults).length === 0
                        ) {
                          alert(
                            "No data to export. Please run the analysis first.",
                          );
                          return;
                        }
                        exportToPDF(
                          selectedCategories,
                          comparisonResult,
                          leadTimesData,
                          tariffs,
                          projectBudget,
                          projectDays,
                          orResults,
                        );
                      }}
                      disabled={
                        !comparisonResult &&
                        leadTimesData.length === 0 &&
                        Object.keys(orResults).length === 0
                      }
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export PDF Report
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (
                          !comparisonResult &&
                          leadTimesData.length === 0 &&
                          Object.keys(orResults).length === 0
                        ) {
                          alert(
                            "No data to export. Please run the analysis first.",
                          );
                          return;
                        }
                        exportToCSV(
                          selectedCategories,
                          comparisonResult,
                          comparisonResult ? leadTimesData : [],
                          tariffs,
                          projectBudget,
                          projectDays,
                          orResults,
                        );
                      }}
                      disabled={
                        !comparisonResult &&
                        leadTimesData.length === 0 &&
                        Object.keys(orResults).length === 0
                      }
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export Excel Data
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {!hasResults && !isAnalyzing && (
              <Card variant="outlined" className="text-center py-12">
                <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  Select materials and run analysis to see feasibility study
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Includes break-even analysis, government incentives, and lead
                  time optimization
                </p>
              </Card>
            )}
          </div>
        </div>

        {/* Results Interpretation Guide - Shows when results are available */}
        {hasResults && (
          <Card
            variant="elevated"
            className="bg-gradient-to-br from-teal-50 to-white border-teal-200"
          >
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-teal-600" />
              Understanding Your Results
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-800 mb-2">
                  What the Numbers Mean
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>
                    <strong>Landed Cost:</strong> Total cost including base
                    price, tariffs, shipping, and customs. This is what you
                    actually pay.
                  </li>
                  <li>
                    <strong>Lead Time:</strong> Days from order to delivery.
                    Critical for project scheduling - late materials delay
                    everything.
                  </li>
                  <li>
                    <strong>Buffer Days:</strong> Safety margin for delivery
                    uncertainty. More buffer = less risk of delays.
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 mb-2">
                  Interpreting ROI for Calgary
                </h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>
                    <strong>6-8% ROI:</strong> Conservative, typical for
                    single-family rentals in established neighborhoods.
                  </li>
                  <li>
                    <strong>8-12% ROI:</strong> Good return, achievable with
                    multi-family or value-add projects.
                  </li>
                  <li>
                    <strong>12%+ ROI:</strong> Excellent, but verify assumptions
                    - may involve higher risk.
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-teal-200">
              <h4 className="font-medium text-gray-800 mb-2">
                Recommended Next Steps
              </h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant="default" size="sm">
                  1. Export your analysis
                </Badge>
                <Badge variant="default" size="sm">
                  2. Review with a financial advisor
                </Badge>
                <Badge variant="default" size="sm">
                  3. Get detailed cost estimates
                </Badge>
                <Badge variant="default" size="sm">
                  4. Verify zoning requirements
                </Badge>
              </div>
            </div>
          </Card>
        )}

        {/* FAQ Section */}
        <Card variant="outlined" className="border-gray-200">
          <button
            onClick={() => setShowFAQ(!showFAQ)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-gray-600" />
              <span className="font-semibold text-gray-900">
                Frequently Asked Questions
              </span>
            </div>
            {showFAQ ? (
              <ChevronUp className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-600" />
            )}
          </button>

          {showFAQ && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
              {feasibilityFAQs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-100 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() =>
                      setExpandedFAQ(expandedFAQ === index ? null : index)
                    }
                    className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">
                      {faq.question}
                    </span>
                    {expandedFAQ === index ? (
                      <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {expandedFAQ === index && (
                    <div className="px-4 pb-4">
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* CTA Section - Professional Consultation */}
        <Card
          variant="elevated"
          className="bg-gradient-to-r from-teal-600 to-teal-700 text-white"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold mb-2">
                Need Help Interpreting Your Results?
              </h3>
              <p className="text-teal-100">
                Our AI building code expert can answer specific questions about
                your Calgary development project, permit requirements, zoning
                compliance, and more.
              </p>
            </div>
            <Link to="/chat" className="flex-shrink-0">
              <Button
                variant="outline"
                className="bg-white text-teal-700 hover:bg-teal-50 border-white"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Chat with AI Expert
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </Card>

        {/* Additional Resources */}
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/quantity-survey" className="block">
            <Card
              variant="outlined"
              className="p-4 hover:border-teal-300 hover:shadow-md transition-all h-full"
            >
              <DollarSign className="h-6 w-6 text-teal-600 mb-2" />
              <h4 className="font-semibold text-gray-900">Cost Estimator</h4>
              <p className="text-sm text-gray-500 mt-1">
                Get detailed construction cost breakdowns with Calgary pricing
              </p>
              <span className="text-sm text-teal-600 mt-2 inline-flex items-center">
                Calculate costs <ArrowRight className="h-3 w-3 ml-1" />
              </span>
            </Card>
          </Link>

          <Link to="/permits/info" className="block">
            <Card
              variant="outlined"
              className="p-4 hover:border-teal-300 hover:shadow-md transition-all h-full"
            >
              <Clock className="h-6 w-6 text-teal-600 mb-2" />
              <h4 className="font-semibold text-gray-900">Permit Guide</h4>
              <p className="text-sm text-gray-500 mt-1">
                Calgary permit requirements, fees, and timeline expectations
              </p>
              <span className="text-sm text-teal-600 mt-2 inline-flex items-center">
                View guide <ArrowRight className="h-3 w-3 ml-1" />
              </span>
            </Card>
          </Link>

          <Link to="/sdab" className="block">
            <Card
              variant="outlined"
              className="p-4 hover:border-teal-300 hover:shadow-md transition-all h-full"
            >
              <AlertTriangle className="h-6 w-6 text-teal-600 mb-2" />
              <h4 className="font-semibold text-gray-900">
                SDAB Risk Analysis
              </h4>
              <p className="text-sm text-gray-500 mt-1">
                Assess development appeal risk for variance requests
              </p>
              <span className="text-sm text-teal-600 mt-2 inline-flex items-center">
                Analyze risk <ArrowRight className="h-3 w-3 ml-1" />
              </span>
            </Card>
          </Link>
        </div>
      </div>
    </>
  );
}
